import { useRef } from 'react';
import { Helmet } from 'react-helmet';
import { IResetPasswordWithTempPasswordProps } from '../../types/propTypes';
import { confirmSignInWithNewPassword } from '../../services/login/login-service';
import { IResetPasswordWithTempPasswordField } from '../../models/login';
import { Formik, Field } from 'formik';
import { Form, Button } from 'react-bootstrap';
import { ResetPasswordWithTempPasswordValidationSchema } from '../../validation/login/login-validation';
import { Common } from '../../utils/constants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { companyNameReplace } from '../../utils';
import images from '../../assets/images';
import { emailNotification } from '../../services/login/loginApi-service';
import ReactstrapFormikFloatingLabelPasswordInput from '../../components/reactstrap-formik/ReactstrapFormikFloatingLabelPasswordInput';
import { showResetPasswordErrorToast } from '../../utils/authErrorToast';

const ResetPasswordWithTempPasswordForm = ({ email, onChangeStage }: IResetPasswordWithTempPasswordProps) => {
  const { t } = useTranslation();
  const strengthRef = useRef('');
  const initialState: IResetPasswordWithTempPasswordField = {
    password: '',
    confirmPassword: '',
  };

  const handleSubmit = async (value: IResetPasswordWithTempPasswordField) => {
    try {
      const emailNotificationInput = {
        email: String(email),
        action_type: Common.passwordChangeAction.FORCE_RESET_PASSWORD,
        while_logged_out: true,
      };
      const response = await confirmSignInWithNewPassword(value.password);
      toast.success(t('forgot.toast.success'));
      await emailNotification(emailNotificationInput);

      const nextStage =
        response.nextStep.signInStep === Common.AuthenticationSteps.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
          ? Common.AuthStage.SIGNIN_OTP
          : Common.AuthStage.SIGNIN;
      onChangeStage(nextStage);
    } catch (error) {
      showResetPasswordErrorToast(error, t);
    }
  };

  return (
    <>
      <Helmet>
        <title>{companyNameReplace(t('forgot.resetTitle'))}</title>
      </Helmet>

      <div className="login-form">
        <div className="title-outer mb-4">
          <h1 className="title">
            <img src={images.logoIconPNG} alt={companyNameReplace(t<string>('login.label.logo'))} />
            {t('forgot.resetTempPasswordHeader')}
          </h1>
        </div>

        <Formik
          initialValues={initialState}
          onSubmit={handleSubmit}
          validationSchema={ResetPasswordWithTempPasswordValidationSchema(t, strengthRef)}
        >
          {(props) => {
            const { handleSubmit, values, isValid } = props;

            let buttonText = t('forgot.button.save');
            let isDisabled = true;

            if (!(values.password && values.confirmPassword)) {
              buttonText = t('forgot.button.enterNewPassword');
              isDisabled = true;
            } else {
              buttonText = t('forgot.button.resetPassword');
              isDisabled = !isValid;
            }

            return (
              <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
                <input type="text" name="username" autoComplete="username" className="hidden-input" />
                <Field
                  type="password"
                  name="password"
                  id="Password"
                  label={t('forgot.form.password')}
                  component={ReactstrapFormikFloatingLabelPasswordInput}
                  autoComplete="new-password"
                  data-testid="password"
                  isShowStrengthBar={true}
                  passwordStrengthRef={strengthRef}
                />
                <Field
                  type="password"
                  name="confirmPassword"
                  id="ConfirmPassword"
                  label={t('forgot.form.confirmPassword')}
                  autoComplete="new-password"
                  component={ReactstrapFormikFloatingLabelPasswordInput}
                  data-testid="confirmPassword"
                />
                <Form.Group className="form-group">
                  <Button
                    type="submit"
                    className={`btn btn-${isDisabled ? 'secondary' : 'primary'} w-100`}
                    disabled={isDisabled}
                  >
                    {buttonText}
                  </Button>
                </Form.Group>
              </form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default ResetPasswordWithTempPasswordForm;
